import { Dispatch } from "react";
import { FileUploadInterface } from "../../middleware/interfaces/file/file-upload-interface";
import { UserInterface } from "../../middleware/interfaces/user/user-interface";
import { ProjectInterface } from "../../middleware/interfaces/project/project-interface";
import { FolderInterface } from "../../middleware/interfaces/folder/folder-interface";
import { FileInterface } from "../../middleware/interfaces/file/file-interface";
import { FileVersionInterface } from "../../middleware/interfaces/file/file-version-interface";
import { NavigateFunction } from "react-router-dom";
import { ReportInterface } from "../../middleware/interfaces/report/report-interface";
import { ForgeInterface } from "../../middleware/interfaces/forge/forge-interface";
import { ClearIntervalID } from "../ui/methods";
import { UserLogOutInterface } from "../../middleware/interfaces/user/user-logout-interface";
import { ReportRetrieveInterface } from "../../middleware/interfaces/report/report-retrieve-interface";
import { UserSubscriptionInterface } from "../../middleware/interfaces/user/user-subscription-interface";

export const CalculateTimeDifferenceInMinutes = (
  startTime: Date,
  endTime: Date
): number => {
  const differenceInMillis =
    new Date(endTime).getTime() - new Date(startTime).getTime();
  const differenceInMinutes = Math.floor(differenceInMillis / (1000 * 60));
  return differenceInMinutes;
};

export const FormatDateFE = (
  date: Date | string,
  excludeTime?: boolean
): string => {
  // Parse the input date
  const inputDate = new Date(date);
  if (isNaN(inputDate.getTime())) {
    return "NaN";
  }

  // Extract local time components
  const localDate = new Date(
    inputDate.getTime() - inputDate.getTimezoneOffset() * 60000
  );

  const year = localDate.getFullYear();
  const month = String(localDate.getMonth() + 1).padStart(2, "0");
  const day = String(localDate.getDate()).padStart(2, "0");

  const hasTime =
    inputDate.getHours() !== 0 ||
    inputDate.getMinutes() !== 0 ||
    inputDate.getSeconds() !== 0;

  let timeString = "";
  if (!excludeTime && (excludeTime === undefined || hasTime)) {
    const hours = String(localDate.getHours()).padStart(2, "0");
    const minutes = String(localDate.getMinutes()).padStart(2, "0");
    const seconds = String(localDate.getSeconds()).padStart(2, "0");
    timeString = ` ${hours}:${minutes}:${seconds}`;
  }

  return `${year}-${month}-${day}${timeString}`;
};

export const FormatDateBE = (date: Date | string): string => {
  const formatedDate = new Date(date);
  const year = formatedDate.getUTCFullYear();
  const month = String(formatedDate.getUTCMonth() + 1).padStart(2, "0");
  const day = String(formatedDate.getUTCDate()).padStart(2, "0");
  const hours = String(formatedDate.getUTCHours()).padStart(2, "0");
  const minutes = String(formatedDate.getUTCMinutes()).padStart(2, "0");
  const seconds = String(formatedDate.getUTCSeconds()).padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

// Files are stored with the name and the extension apart (see PrepareUploadFile).
export const FileFullName = (fileName: string, fileType: string): string => {
  return fileType ? `${fileName}.${fileType}` : fileName;
};

// Names like Concrete_Estimating_Template have no spaces, so a wrapped label
// would split mid-word. A zero-width space after each _ or - lets the line break
// there instead. Not after a dot, so an extension stays on the line with the
// end of the name. Display only: titles and anything sent to the server keep
// the real name.
export const BreakableName = (name: string): string => {
  return name.replace(/([_-])/g, "$1\u200B");
};

// What a file's extension means, for the formats people bring here. Anything
// else is shown by its extension alone.
const FILE_FORMAT_NAMES: Record<string, string> = {
  ifc: "IFC model",
  rvt: "Revit project",
  rte: "Revit template",
  rfa: "Revit family",
  nwd: "Navisworks document",
  nwc: "Navisworks cache",
  nwf: "Navisworks file set",
  dwg: "AutoCAD drawing",
  dxf: "DXF drawing",
  pdf: "PDF document",
  xlsx: "Excel workbook",
  csv: "CSV table",
  docx: "Word document",
  zip: "ZIP archive",
};

export const FileFormatLabel = (fileType: string): string => {
  const extension = fileType?.toUpperCase() ?? "";
  const formatName = FILE_FORMAT_NAMES[fileType?.toLowerCase()];
  return formatName ? `${extension} (${formatName})` : extension;
};

export const BytesToReadableSize = (bytes: number): string => {
  if (bytes >= 1024 * 1024 * 1024) {
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + " GB";
  } else {
    return (bytes / (1024 * 1024)).toFixed(2) + " MB";
  }
};

export const PrepareUploadFile = (
  uploadFile: any,
  user: UserInterface,
  navigate: NavigateFunction,
  dispatch: Dispatch<any>
) => {
  const uploadFileName = uploadFile.name.split(".")[0].toLowerCase();
  const uploadFileType = uploadFile.name.split(".").reverse()[0].toLowerCase();
  const uploadFileSize = uploadFile.size;

  // The File is passed along untouched. Reading it into a base64 string here
  // used to cost three full copies of the file in memory -- the ArrayBuffer,
  // the data URL, and the split() result -- before a single byte was sent.
  if (uploadFileType === "ifc") {
    navigate("/STANDARD");
    dispatch({
      type: "OPEN_MODEL",
      payload: {
        modelName: uploadFileName,
        modelFileData: uploadFile,
        modelFileSize: uploadFileSize,
      },
      response: "WAIT",
    });
  } else {
    const uploadFileData: FileUploadInterface = {
      email: user.email,
      projectID: user.openProject!.projectID!,
      projectUserRole: user.openProject!.projectUserRole,
      fileParentID: user.openFolder ? user.openFolder.folderID : null,
      fileName: uploadFileName,
      fileType: uploadFileType,
      fileSize: uploadFileSize,
      fileUploadDate: FormatDateBE(new Date().toISOString()),
      fileData: uploadFile,
      type: "FileUploadInterface",
    };
    dispatch({ type: "UPLOAD_FILE", payload: uploadFileData });
  }
};

export const UpdateUserWithServerResponse = async (
  serverResponse: any,
  dispatch: Dispatch<any>,
  user?: UserInterface
) => {
  if (serverResponse.header === "authentication") {
    const userData = serverResponse.data;
    const updatedUser: UserInterface = {
      userName: userData["UserInformation"]["UserName"],
      email: userData["UserInformation"]["Email"],
      company: userData["UserInformation"]["Company"],
      ...(userData["UserInformation"]["Subscription"] && {
        subscription: ParseSubscriptionData(
          userData["UserInformation"]["Subscription"]
        ),
      }),
      lastActionDate: FormatDateBE(new Date()),
      projects: ConvertRawDataToProjectsArray(
        Object.values(userData["ProjectInformation"])
      ),
      type: "UserInterface",
    };

    if (userData["OpenProject"] || user?.openProject) {
      const projectID = user?.openProject?.projectID ?? userData["OpenProject"];
      updatedUser.openProject = updatedUser.projects.find(
        (project) => project.projectID === projectID
      );

      if (userData["OpenFolder"] || user?.openFolder) {
        const folderID = user?.openFolder?.folderID ?? userData["OpenFolder"];
        updatedUser.openFolder = FindFolderByID(
          updatedUser.openProject?.projectFolders,
          folderID
        );
      }

      if (userData["OpenReport"]) {
        const openReportData: ReportRetrieveInterface = {
          email: updatedUser.email,
          reportID: userData["OpenReport"],
          projectID: updatedUser.openProject?.projectID!,
          type: "ReportRetrieveInterface",
        };
        dispatch({
          type: "RETRIEVE_REPORT",
          payload: openReportData,
        });
      }
    }

    dispatch({
      type: "UPDATE_USER",
      payload: updatedUser,
      response: "",
    });
  } else if (serverResponse.header === "message") {
    dispatch({
      type: "RESPONSE",
      response: serverResponse.message,
    });
  } else if (serverResponse.header === "logout") {
    ClearIntervalID();
    // dispatch({
    //   type: "RESPONSE",
    //   response: serverResponse.message,
    // });
    dispatch({
      type: "CLEAR_STATE",
      response: serverResponse.message,
    });
  }
};

function ConvertRawDataToProjectsArray(rawProjects: any[]): ProjectInterface[] {
  return rawProjects.map(ConvertRawDataToProjectInterface);
}

function ConvertRawDataToProjectInterface(rawData: any): ProjectInterface {
  return {
    projectID: rawData.ProjectID,
    projectName: rawData.ProjectName,
    projectLocation: rawData.ProjectLocation,
    projectDescription: rawData.ProjectDescription,
    email: rawData.ProjectOwner,
    projectUserRole: rawData.ProjectUserRole,
    projectStartDate: rawData.ProjectStartDate,
    projectEndDate: rawData.ProjectEndDate,
    projectCreationDate: rawData.ProjectCreationDate,
    projectFiles: ConvertRawFiles(rawData.ProjectFiles || {}),
    projectFolders: ConvertRawFolders(rawData.ProjectFolders),
    projectReports: ConvertRawReports(rawData.ProjectReports),
    projectForges: ConvertRawForges(rawData.ProjectForges || []),
    projectContentFilter: {
      reports: true,
      forges: true,
      folders: true,
      filesAndModels: {
        models: true,
        files: true,
      },
      type: "ProjectContentFilterInterface",
    },
    type: "ProjectInterface",
  };
}

function ConvertRawDataToFileInterface(rawFile: any): FileInterface {
  const fileVersions = Object.entries(JSON.parse(rawFile.FileVersions)).reduce(
    (acc, [key, value]) => {
      const castValue = value as Omit<FileVersionInterface, "type">;
      return {
        ...acc,
        [key]: {
          fileSize: castValue.fileSize,
          fileUploadDate: new Date(castValue.fileUploadDate),
          fileUploadedBy: castValue.fileUploadedBy,
          type: "FileVersionInterface",
        },
      };
    },
    {}
  );
  return {
    projectID: rawFile.ProjectID,
    fileID: rawFile.FileID,
    fileParentID: rawFile.FileParentID,
    fileName: rawFile.FileName,
    fileType: rawFile.FileType,
    fileActiveVersion: Object.keys(fileVersions).length,
    fileVersions: fileVersions,
    fileSize: rawFile.FileSize,
    forgeID: rawFile.ForgeID ?? null,
    fileUploadDate: new Date(rawFile.FileUploadDate),
    fileUploadedBy: rawFile.FileUploadedBy,
    type: "FileInterface",
  };
}

function ConvertRawFiles(rawFilesObject: {
  [key: number]: any;
}): FileInterface[] {
  // Convert object values to array
  const rawFilesArray = Object.values(rawFilesObject);
  return rawFilesArray.map((rawFile) => ConvertRawDataToFileInterface(rawFile));
}

function ConvertRawFolders(rawFoldersObject: {
  [key: number]: any;
}): FolderInterface[] {
  const rawFoldersArray = Object.values(rawFoldersObject);

  return rawFoldersArray.map((rawFolder) => {
    // Initialize folder without children
    const folder: FolderInterface = {
      folderID: rawFolder.FolderID,
      projectID: rawFolder.ProjectID,
      folderParentID: rawFolder.FolderParentID,
      folderFiles: rawFolder.FolderFiles,
      folderChildren: rawFolder.FolderChildren, // Temporarily set as empty
      folderName: rawFolder.FolderName,
      folderCreationDate: new Date(rawFolder.FolderCreationDate),
      folderCreatedBy: rawFolder.FolderCreatedBy,
      folderForged: rawFolder.FolderForged ?? false,
      type: "FolderInterface",
    };

    return folder;
  });
}

function ConvertRawReports(rawReportsObject: {
  [key: number]: any;
}): ReportInterface[] {
  const rawReportsArray = Object.values(rawReportsObject);

  return rawReportsArray.map((rawReport) => {
    const report: ReportInterface = {
      reportID: rawReport.ReportID,
      projectID: rawReport.ProjectID,
      reportName: rawReport.ReportName,
      reportSize: rawReport.ReportSize,
      reportCreationDate: new Date(rawReport.ReportCreationDate),
      reportUploadedBy: rawReport.ReportUploadedBy,
      reportClashCount: rawReport.ReportClashCount,
      type: "ReportInterface",
    };

    return report;
  });
}

function ConvertRawForges(rawForgesObject: {
  [key: number]: any;
}): ForgeInterface[] {
  const rawForgesArray = Object.values(rawForgesObject);

  return rawForgesArray.map((rawForge) => {
    const forge: ForgeInterface = {
      forgeID: rawForge.ForgeID,
      projectID: rawForge.ProjectID,
      forgeName: rawForge.ForgeName,
      forgeOrigin: rawForge.ForgeOrigin,
      forgeDestination: rawForge.ForgeDestination,
      forgeDescription: rawForge.ForgeDescription,
      forgeStatus: rawForge.ForgeStatus,
      forgeRunFrequency: rawForge.ForgeRunFrequency,
      forgeCreationDate: new Date(rawForge.ForgeCreationDate),
      forgeLastRunDate: rawForge.ForgeLastRunDate
        ? new Date(rawForge.ForgeLastRunDate)
        : null,
      forgeLastRunStatus: rawForge.ForgeLastRunStatus ?? null,
      forgeLastRunError: rawForge.ForgeLastRunError ?? null,
      forgeRunRequested: rawForge.ForgeRunRequested
        ? new Date(rawForge.ForgeRunRequested)
        : null,
      forgeRunTime: rawForge.ForgeRunTime ?? "09:00:00",
      type: "ForgeInterface",
    };

    return forge;
  });
}

/**
 * A forge's run time is stored in UTC, because the database is, but nobody
 * schedules a nightly transfer by working out what 09:00 UTC is where they
 * live. These two convert at the edge so the dialog can deal purely in the
 * viewer's own clock.
 *
 * Both anchor the conversion to today, so a stored time follows the viewer's
 * current offset. That means a forge set for 2am keeps saying 2am across a
 * daylight-saving change while the UTC hour behind it stays put, so the run
 * itself moves by an hour twice a year. Harmless for an overnight transfer, and
 * the alternative is storing a timezone per forge.
 */
export function UtcTimeToLocalHour(utcTime: string): number {
  const [hours, minutes] = utcTime.split(":").map(Number);
  const anchored = new Date();
  anchored.setUTCHours(hours, minutes ?? 0, 0, 0);
  // Rounded, not truncated. A stored time only fails to land on a whole local
  // hour where the offset is not whole either -- +05:30 turns 09:00 UTC into
  // 14:30 -- and that happens solely to rows the dialog never wrote, since it
  // only ever sends a whole local hour. Truncating showed 2:00 for a 2:30 run
  // and quietly moved it half an hour earlier on the next unrelated save.
  return (anchored.getHours() + Math.round(anchored.getMinutes() / 60)) % 24;
}

export function LocalHourToUtcTime(localHour: number): string {
  const anchored = new Date();
  anchored.setHours(localHour, 0, 0, 0);
  const hours = String(anchored.getUTCHours()).padStart(2, "0");
  // Not always :00 -- offsets like +05:30 put the UTC equivalent on the half
  // hour, and rounding it away would move the run by thirty minutes.
  const minutes = String(anchored.getUTCMinutes()).padStart(2, "0");
  return `${hours}:${minutes}:00`;
}

export function FindFolderByID(
  folders: FolderInterface[] | undefined | null,
  folderID: number | undefined | null
): FolderInterface | undefined {
  if (!folders || !folderID) {
    return undefined; // Return undefined if folders is undefined
  }

  for (const folder of folders) {
    if (folder.folderID === folderID) {
      return folder; // Found the folder
    }

    // // If not found, recursively search in the children
    // const foundInChildren = FindFolderByID(folder.folderChildren, folderID);
    // if (foundInChildren) {
    //   return foundInChildren; // Found the folder in children
    // }
  }

  // If the folder is not found in this branch
  return undefined;
}

export function UserLogOut(dispatch: Dispatch<any>) {
  const userLogOutData: UserLogOutInterface = {
    type: "UserLogOutInterface",
  };
  dispatch({ type: "LOGOUT", payload: userLogOutData });
}

function ParseSubscriptionData(
  subscription: Record<string, any>
): UserSubscriptionInterface {
  return {
    userSubscriptionPlan: subscription["SubscriptionPlan"],
    userSubscriptionStatus: subscription["SubscriptionStatus"],
    userSubscriptionStartDate: subscription["SubscriptionStartDate"],
    userSubscriptionEndDate: subscription["SubscriptionEndDate"],
    userSubscriptionTotalSpaceAvailable: subscription["TotalSpaceAvailable"],
    userSubscriptionTotalSpaceUsed: subscription["TotalSpaceUsed"],
    type: "UserSubscriptionInterface",
  };
}
